import React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';
import Icon from '../AppIcon';

const buttonVariants = cva(
  'cp07-button inline-flex items-center justify-center whitespace-nowrap text-[12px] font-semibold ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/25 focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'cp07-btn-primary text-white',
        destructive: 'cp07-btn-danger text-white',
        outline: 'cp07-btn-outline text-slate-700',
        secondary: 'cp07-btn-secondary text-slate-800',
        ghost: 'cp07-btn-ghost text-slate-700',
        link: 'cp08-link underline-offset-4 hover:underline',
        success: 'cp07-btn-success text-white',
        warning: 'cp07-btn-warning text-white',
        danger: 'cp07-btn-danger text-white',
      },
      size: {
        default: 'h-9 rounded-xl px-4 py-2',
        sm: 'h-8 rounded-lg px-3 text-[11px]',
        lg: 'h-10 rounded-xl px-6 text-sm',
        icon: 'h-9 w-9 rounded-xl',
        xs: 'h-8 rounded-lg px-3 text-xs',
        xl: 'h-11 rounded-xl px-7 text-sm',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

const Button = React.forwardRef(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      children,
      loading = false,
      iconName = null,
      iconPosition = 'left',
      iconSize = null,
      fullWidth = false,
      disabled = false,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';

    const iconSizeMap = {
      xs: 12,
      sm: 14,
      default: 16,
      lg: 18,
      xl: 20,
      icon: 16,
    };

    const calculatedIconSize = iconSize || iconSizeMap?.[size] || 16;

    const LoadingSpinner = () => (
      <svg
        className="animate-spin -ml-1 mr-2 h-4 w-4"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    );

    const renderIcon = () => {
      if (!iconName) return null;

      return (
        <Icon
          name={iconName}
          size={calculatedIconSize}
          className={cn(
            children && iconPosition === 'left' && 'mr-2',
            children && iconPosition === 'right' && 'ml-2'
          )}
        />
      );
    };

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          fullWidth && 'w-full'
        )}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && <LoadingSpinner />}
        {iconName && iconPosition === 'left' && renderIcon()}
        {children}
        {iconName && iconPosition === 'right' && renderIcon()}
      </Comp>
    );
  }
);

Button.displayName = 'Button';

export default Button;
